import type { ApplyTerritoryResponse, CreateTerritoryBody, TerritoryAdminDTO, TerritoryRegionInput } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Field, IconButton, Input, MultiSelect, Select, useConfirm, useToast } from '../../../components/ui';
import { useLookups } from '../../../hooks/useMeta';
import { api, errorMessage } from '../../../lib/api';
import { pluralize } from '../../../lib/format';

type Draft = { name: string; defaultOwnerId: string; memberIds: string[]; regions: { country: string; region: string; city: string }[] };

const toDraft = (t: TerritoryAdminDTO | null): Draft => ({
  name: t?.name ?? '',
  defaultOwnerId: t?.defaultOwnerId ?? '',
  memberIds: t?.memberIds ?? [],
  regions: t?.regions.map((r) => ({ country: r.country, region: r.region ?? '', city: r.city ?? '' })) ?? [{ country: 'CA', region: '', city: '' }],
});

/** Areas (country → province → city) that route new leads to a salesperson. */
export function TerritoriesSection() {
  const { data } = useQuery({ queryKey: ['settings', 'territories'], queryFn: ({ signal }) => api.get<{ items: TerritoryAdminDTO[] }>('/api/territories', { signal }) });
  const [editing, setEditing] = useState<TerritoryAdminDTO | 'new' | null>(null);
  return (
    <>
      <Card>
        <CardHeader
          title="Territories"
          description="New leads in a territory's area get its default salesperson. “Apply” also assigns existing unassigned leads."
          actions={
            <Button size="sm" variant="primary" icon={Plus} onClick={() => setEditing('new')}>
              Add territory
            </Button>
          }
        />
        <ul className="divide-y divide-slate-100">
          {(data?.items ?? []).map((territory) => (
            <li key={territory.id}>
              <button type="button" className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left text-[13px] hover:bg-slate-50" onClick={() => setEditing(territory)}>
                <MapPin className="size-4 text-slate-400" />
                <span className="font-medium text-slate-900">{territory.name}</span>
                <span className="text-slate-500">{territory.regions.map((r) => [r.city, r.region, r.country].filter(Boolean).join(', ')).join(' · ') || 'No areas'}</span>
                <span className="ml-auto text-xs text-slate-500">{pluralize(territory.leadCount, 'lead')}</span>
              </button>
            </li>
          ))}
          {data && data.items.length === 0 && <li className="px-4 py-6 text-center text-[13px] text-slate-500">No territories yet.</li>}
        </ul>
      </Card>
      {editing && <TerritoryEditor key={editing === 'new' ? 'new' : editing.id} territory={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
    </>
  );
}

function TerritoryEditor({ territory, onDone }: { territory: TerritoryAdminDTO | null; onDone: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { meta } = useLookups();
  const users = (meta?.users ?? []).filter((u) => u.isActive);
  const [draft, setDraft] = useState<Draft>(toDraft(territory));
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['settings', 'territories'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const body = (): CreateTerritoryBody => ({
    name: draft.name,
    defaultOwnerId: draft.defaultOwnerId || null,
    memberIds: draft.memberIds,
    regions: draft.regions.filter((r) => r.country.trim()).map((r): TerritoryRegionInput => ({ country: r.country, region: r.region || null, city: r.city || null })),
  });
  const save = useMutation({
    mutationFn: () => (territory ? api.patch(`/api/territories/${territory.id}`, body()) : api.post('/api/territories', body())),
    onSuccess: async () => {
      toast.success('Territory saved');
      await refresh();
      onDone();
    },
    onError,
  });
  const apply = useMutation({
    mutationFn: () => api.post<ApplyTerritoryResponse>(`/api/territories/${territory!.id}/apply`),
    onSuccess: async (result) => {
      toast.success(`${pluralize(result.assigned, 'lead')} linked, ${result.ownersSet} assigned to the default salesperson`);
      await refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/territories/${territory!.id}`),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
    onError,
  });
  const setRegion = (index: number, patch: Partial<Draft['regions'][number]>) => setDraft({ ...draft, regions: draft.regions.map((r, i) => (i === index ? { ...r, ...patch } : r)) });

  return (
    <Card>
      <CardHeader title={territory ? `Edit “${territory.name}”` : 'New territory'} />
      <CardBody className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Montréal & Laval" />
          </Field>
          <Field label="Default salesperson">
            <Select value={draft.defaultOwnerId} onChange={(e) => setDraft({ ...draft, defaultOwnerId: e.target.value })}>
              <option value="">None</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Team members" className="sm:col-span-2">
            <MultiSelect label="Members" options={users.map((u) => ({ value: u.id, label: u.name }))} value={draft.memberIds} onChange={(memberIds) => setDraft({ ...draft, memberIds })} />
          </Field>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium text-slate-500">Areas (empty province or city = all of it)</p>
          {draft.regions.map((region, index) => (
            <div key={index} className="mb-2 flex gap-2">
              <Input size="sm" className="w-20" aria-label="Country" value={region.country} maxLength={2} onChange={(e) => setRegion(index, { country: e.target.value.toUpperCase() })} />
              <Input size="sm" className="w-40" aria-label="Province or state" placeholder="Province / state" value={region.region} onChange={(e) => setRegion(index, { region: e.target.value })} />
              <Input size="sm" className="w-48" aria-label="City" placeholder="City" value={region.city} onChange={(e) => setRegion(index, { city: e.target.value })} />
              <IconButton icon={Trash2} label="Remove area" size="sm" variant="ghost" onClick={() => setDraft({ ...draft, regions: draft.regions.filter((_, i) => i !== index) })} />
            </div>
          ))}
          <Button size="sm" icon={Plus} onClick={() => setDraft({ ...draft, regions: [...draft.regions, { country: 'CA', region: '', city: '' }] })}>
            Add area
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" loading={save.isPending} disabled={!draft.name.trim()} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Close
          </Button>
          {territory && (
            <>
              <Button loading={apply.isPending} onClick={() => apply.mutate()}>
                Apply to existing leads
              </Button>
              <Button
                variant="ghost"
                icon={Trash2}
                onClick={() =>
                  void (async () => {
                    if (await confirm({ title: `Delete “${territory.name}”?`, message: 'Leads keep their salesperson; they just lose the territory link.', confirmLabel: 'Delete', danger: true })) remove.mutate();
                  })()
                }
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
